import { useEffect, useRef, useState } from 'react';
import { TongueScene } from '@/ui/tongue3d/TongueScene';

/** The offsets built into TongueScene (HEAD_BACK_MM, LOWER_FORWARD_MM), for the readout. */
const HEAD_BACK_MM = 5.5;
const LOWER_FORWARD_MM = 2;
/** In development, a local reference image is loaded from here if present (git-ignored). */
const LOCAL_REFERENCE = '/data/qa/xray-reference.webp';

interface Overlay {
  x: number;
  y: number;
  scale: number;
  rotate: number;
  opacity: number;
  flip: boolean;
}

/**
 * Development page (`?align`): fits the 3D mouth to a side X-ray (or any side reference). The
 * model is shown straight from the side with a narrow field of view (close to an X-ray's
 * projection), the reference over it, half see-through. First move and scale the reference
 * (drag, mouse wheel, or the sliders) until its face profile matches the model's; the face then
 * stays put while "Mouth" moves the teeth, palate and tongue against it, and "Lower jaw" moves
 * the lower teeth against the upper ones. The readout gives the numbers to build in. The image is
 * picked locally and never uploaded or committed.
 */
export function AlignPreview() {
  const host = useRef<HTMLDivElement>(null);
  const scene = useRef<TongueScene | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<Overlay>({
    x: 0,
    y: 0,
    scale: 1,
    rotate: 0,
    opacity: 0.5,
    flip: true,
  });
  const [mouth, setMouth] = useState<[number, number]>([0, 0]);
  const [lower, setLower] = useState<[number, number]>([0, 0]);
  const [skin, setSkin] = useState(true);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    if (!host.current) return;
    const s = new TongueScene(host.current, { orbitControls: false });
    scene.current = s;
    s.setBlend('rest', 'rest', 1);
    if (import.meta.env.DEV)
      void fetch(LOCAL_REFERENCE, { method: 'HEAD' }).then((r) => {
        if (r.ok && (r.headers.get('content-type') ?? '').startsWith('image'))
          setImage(LOCAL_REFERENCE);
      });
    return () => {
      s.dispose();
    };
  }, []);

  // The face stays put on screen: it and the camera move together; the mouth moves against them.
  useEffect(() => {
    const s = scene.current;
    if (!s) return;
    void s.ready.then(() => {
      s.setFit({
        face: [-HEAD_BACK_MM - mouth[0], -mouth[1]],
        lower: [LOWER_FORWARD_MM + lower[0], lower[1]],
      });
      s.setView({
        azimuth: 0,
        elevation: 0,
        distance: 0.75,
        fov: 8,
        focus: [0, -4 - mouth[1], -40 - mouth[0]],
      });
    });
  }, [mouth, lower]);

  useEffect(() => {
    const s = scene.current;
    if (!s) return;
    void s.ready.then(() => {
      s.setSkinVisible(skin);
    });
  }, [skin]);

  const slider = (
    label: string,
    value: number,
    set: (v: number) => void,
    min: number,
    max: number,
    step: number,
  ) => (
    <label
      style={{ display: 'grid', gridTemplateColumns: '9em 1fr 4em', gap: 8, alignItems: 'center' }}
    >
      {label}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => {
          set(Number(e.currentTarget.value));
        }}
      />
      <span style={{ fontFamily: 'monospace' }}>{value.toFixed(step < 1 ? 2 : 0)}</span>
    </label>
  );

  const readout = [
    `HEAD_BACK_MM = ${(HEAD_BACK_MM + mouth[0]).toFixed(1)}`,
    `face up (mm) = ${(-mouth[1]).toFixed(1)}`,
    `LOWER_FORWARD_MM = ${(LOWER_FORWARD_MM + lower[0]).toFixed(1)}`,
    `lower up (mm) = ${lower[1].toFixed(1)}`,
  ].join('\n');

  return (
    <main
      style={{
        padding: 16,
        background: '#0b1a3a',
        minHeight: '100vh',
        color: 'white',
        font: '14px system-ui',
      }}
    >
      <h1 style={{ fontSize: 18, margin: '0 0 8px' }}>Align the mouth to a side X-ray</h1>
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 900,
          aspectRatio: '4 / 3',
          background: '#e8f0fb',
          borderRadius: 12,
          overflow: 'hidden',
          touchAction: 'none',
        }}
        onPointerDown={(e) => {
          drag.current = { x: e.clientX, y: e.clientY, ox: overlay.x, oy: overlay.y };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (d) setOverlay({ ...overlay, x: d.ox + e.clientX - d.x, y: d.oy + e.clientY - d.y });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onWheel={(e) => {
          setOverlay({
            ...overlay,
            scale: Math.max(0.05, overlay.scale * (e.deltaY < 0 ? 1.03 : 1 / 1.03)),
          });
        }}
      >
        <div ref={host} style={{ position: 'absolute', inset: 0 }} />
        {image && (
          <img
            src={image}
            alt="Reference"
            draggable={false}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: '100%',
              pointerEvents: 'none',
              opacity: overlay.opacity,
              transform: `translate(-50%, -50%) translate(${String(overlay.x)}px, ${String(overlay.y)}px) rotate(${String(overlay.rotate)}deg) scale(${String(overlay.flip ? -overlay.scale : overlay.scale)}, ${String(overlay.scale)})`,
            }}
          />
        )}
      </div>
      <div style={{ display: 'grid', gap: 6, maxWidth: 900, marginTop: 12 }}>
        <label>
          Reference image{' '}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) setImage(URL.createObjectURL(f));
            }}
          />
        </label>
        <strong>Reference (match its face profile to the model's)</strong>
        {slider(
          'Scale',
          overlay.scale,
          (v) => {
            setOverlay({ ...overlay, scale: v });
          },
          0.05,
          4,
          0.01,
        )}
        {slider(
          'Rotate (°)',
          overlay.rotate,
          (v) => {
            setOverlay({ ...overlay, rotate: v });
          },
          -30,
          30,
          0.5,
        )}
        {slider(
          'Opacity',
          overlay.opacity,
          (v) => {
            setOverlay({ ...overlay, opacity: v });
          },
          0,
          1,
          0.05,
        )}
        <label>
          <input
            type="checkbox"
            checked={overlay.flip}
            onChange={(e) => {
              setOverlay({ ...overlay, flip: e.currentTarget.checked });
            }}
          />{' '}
          Mirror the reference (the model faces right)
        </label>
        <label>
          <input
            type="checkbox"
            checked={skin}
            onChange={(e) => {
              setSkin(e.currentTarget.checked);
            }}
          />{' '}
          Show the face
        </label>
        <strong>Mouth against the face (mm)</strong>
        {slider(
          'Forward',
          mouth[0],
          (v) => {
            setMouth([v, mouth[1]]);
          },
          -15,
          15,
          0.5,
        )}
        {slider(
          'Up',
          mouth[1],
          (v) => {
            setMouth([mouth[0], v]);
          },
          -15,
          15,
          0.5,
        )}
        <strong>Lower jaw against the upper teeth (mm)</strong>
        {slider(
          'Forward',
          lower[0],
          (v) => {
            setLower([v, lower[1]]);
          },
          -8,
          8,
          0.25,
        )}
        {slider(
          'Up',
          lower[1],
          (v) => {
            setLower([lower[0], v]);
          },
          -8,
          8,
          0.25,
        )}
        <pre style={{ background: '#132650', padding: 8, borderRadius: 6 }}>{readout}</pre>
      </div>
    </main>
  );
}
